import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { InputField } from "@/components/input-field/InputField";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { ActiveDirectoryTechnicianUser } from "@/services/technicians-ad.service";
import { AppColors } from "@/styles/colors";
import { isEmpty } from "@/utils/query-string";
import { Card, Field, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { technicianUpdateSchema } from "../schema/technicians.schema";
import {
  fetchDefaultADUsersHandler,
  fetchTechnicianHandler,
  getADOptionsHandler,
  loadADOptionsHandler,
  submitUpdateTechnicianHandler,
} from "../schema/handler-functions";

type TechnicianEditForm = {
  usuario?: Option | null;
  nombreApellido?: string;
  email?: string;
  activo?: unknown;
};

export const TechnicianEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  useEffect(() => {
    setIsActive(true);
  }, []);

  const { control, setValue, handleSubmit } = useForm<TechnicianEditForm>({
    resolver: zodResolver(technicianUpdateSchema),
    defaultValues: {
      usuario: null,
      nombreApellido: "",
      email: "",
      activo: [],
    },
  });

  const watched = useWatch({ control });

  const [loadingSave, setLoadingSave] = useState(false);

  const [loadingADUsers, setLoadingADUsers] = useState(false);
  const [defaultADUsers, setDefaultADUsers] = useState<
    ActiveDirectoryTechnicianUser[]
  >([]);

  const [loadingTechnician, setLoadingTechnician] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadADOptions = (inputValue: string): Promise<Option[]> => {
    return loadADOptionsHandler({
      timeoutRef,
      inputValue,
      setLoadingADUsers,
      getADOptions: async (value) => getADOptionsHandler({ inputValue: value }),
    });
  };

  useEffect(() => {
    if (!isActive) return;
    fetchDefaultADUsersHandler({
      setLoadingADUsers,
      setDefaultADUsers,
    });
  }, [isActive]);

  useEffect(() => {
    if (!isActive || !id) return;

    fetchTechnicianHandler({
      id: Number(id),
      setLoadingTechnician,
      setValue,
      navigate,
      fetchPermissions,
    });
  }, [isActive, id]);

  const handleCancel = () => {
    navigate(AppRoutes.TECHNICIANS);
  };

  const hasNoFilledValues = useMemo(() => {
    const name = watched?.nombreApellido;
    return isEmpty(name);
  }, [watched?.nombreApellido]);

  const onSubmit = async (values: TechnicianEditForm) => {
    if (!id) return;
    await submitUpdateTechnicianHandler({
      id: Number(id),
      values,
      setLoadingSave,
      navigate,
      fetchPermissions,
    });
  };

  const defaultADOptions = useMemo(() => {
    return (
      defaultADUsers?.map((u) => ({
        label: `${u.displayName} - ${u.mail}`,
        value: u.id,
      })) ?? []
    );
  }, [defaultADUsers]);

  const loadingPage = loadingTechnician || loadingADUsers;

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      height="100%"
      minHeight={0}
      data-testid="technicians-edit-page"
    >
      <SectionHeader
        title="Maestro de técnicos"
        addButtonLabel="Guardar"
        addButtonIcon={<Save />}
        cancelButtonLabel="Cancelar"
        handleAdd={handleSubmit(onSubmit)}
        handleCancel={handleCancel}
        loading={loadingSave || loadingPage}
        disableAdd={hasNoFilledValues || loadingSave || loadingPage}
      />

      {loadingPage ? null : (
        <Flex flex="1" minHeight={0} overflowY="auto" flexDir="column" gap="16px">
          <Card.Root maxW="full" borderRadius="8px">
            <Card.Body
              paddingX="48px"
              paddingY="30px"
              borderRadius="8px"
              border={`1px solid ${AppColors.WHITE_BORDER_1}`}
            >
              <Grid templateColumns="repeat(4, 1fr)" gap="32px">
                <GridItem colSpan={1}>
                  <Field.Root>
                    <Field.Label>
                      <Text
                        fontWeight={600}
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        Usuario (Active Directory)
                      </Text>
                    </Field.Label>
                    <AsyncAutocompleteSelect
                      control={control as any}
                      defaultOptions={defaultADOptions}
                      loadOptions={loadADOptions}
                      name="usuario"
                      placeholder="Seleccionar"
                      isLoading={loadingADUsers}
                      closeMenuOnSelect
                      triggerWithLength={false}
                      disabled
                      cacheOptions={false}
                    />
                  </Field.Root>
                </GridItem>

                <GridItem colSpan={1}>
                  <InputField
                    control={control as any}
                    inputName="nombreApellido"
                    labelText="Nombre"
                    placeholder="Ingresar"
                    required
                    disabled
                    minHeight="40px"
                  />
                </GridItem>

                <GridItem colSpan={1}>
                  <InputField
                    control={control as any}
                    inputName="email"
                    labelText="Email"
                    placeholder="Ingresar"
                    disabled
                    minHeight="40px"
                  />
                </GridItem>

                <GridItem colSpan={1}>
                  <SelectComponent
                    control={control as any}
                    name="activo"
                    items={[
                      { label: "Activo", value: true },
                      { label: "Inactivo", value: false },
                    ]}
                    required
                    labelText="Estado"
                    minHeight="40px"
                    disabled={loadingSave || loadingPage}
                  />
                </GridItem>
              </Grid>
            </Card.Body>
          </Card.Root>
        </Flex>
      )}
    </Flex>
  );
};
